import { useState, useEffect } from 'react';
import { fetchCampaignActivityLog, fetchCampaignRequestActivityLog } from '~/services/campaign';
import { formatDateIfValid } from '~/utils';
import LogoIcon from '~/images/logo/logo-icon.svg';

interface ActivityLogChange {
    field: string;
    field_label: string;
    old_value: string;
    new_value: string;
    category: string;
}

interface ActivityLogEntry {
    id: number;
    event_type: string;
    event_category: string;
    title: string;
    description: string;
    person_name: string;
    person_id: number | null;
    user_id: number | null;
    is_staff: boolean;
    created_date: string;
    changes_text: string;
    model_name: string;
    action: string;
    metadata: {
        changes: ActivityLogChange[];
        grouped_changes: Record<string, string[]>;
    };
}

interface PaginationInfo {
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
}

interface ActivityLogProps {
    campaignId?: number | string;
    campaignRequestId?: number | string;
    refreshKey?: number;
    onCountChange?: (count: number) => void;
}

const PAGE_SIZE = 20;
const COLLAPSED_FIELD_LIMIT = 3;
const LONG_TEXT_FIELDS = new Set(['story']);

const formatChangeValue = (value: string | null | undefined): string => {
    if (value === null || value === undefined || value === '' || value === 'None') {
        return '';
    }
    // Boolean-like values
    if (value === 'True' || value === 'true') return 'Yes';
    if (value === 'False' || value === 'false') return 'No';
    return value;
};

// Normalize legacy team-member description text so old entries render with
// the same access-level wording as new ones ("with full team member" → "— Full Access").
const ACCESS_LABEL_MAP: Array<[RegExp, string]> = [
    [/\s+with\s+full team member\s*$/i, ' — Full Access'],
    [/\s+with\s+limited team member\s*$/i, ' — Limited Access'],
    [/\s+with\s+no access team member\s*$/i, ' — No Access'],
];

const normalizeTeamMemberDescription = (description: string): string => {
    if (!description) return description;
    for (const [pattern, replacement] of ACCESS_LABEL_MAP) {
        if (pattern.test(description)) {
            return description.replace(pattern, replacement);
        }
    }
    return description;
};

const ActivityLog = ({ campaignId, campaignRequestId, refreshKey = 0, onCountChange }: ActivityLogProps) => {
    const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());

    useEffect(() => {
        // Reset to page 1 when campaign changes or refresh is triggered
        setCurrentPage(1);
    }, [campaignId, campaignRequestId, refreshKey]);

    useEffect(() => {
        const loadActivityLog = async () => {
            setLoading(true);
            setError(null);

            try {
                let response;
                if (campaignId) {
                    response = await fetchCampaignActivityLog(
                        campaignId,
                        'all',
                        currentPage,
                        PAGE_SIZE,
                        true // include_all to get all related model logs
                    );
                } else if (campaignRequestId) {
                    response = await fetchCampaignRequestActivityLog(
                        campaignRequestId,
                        currentPage,
                        PAGE_SIZE
                    );
                } else {
                    setLoading(false);
                    return;
                }
                setEntries(response.results || []);
                setPagination({
                    count: response.count,
                    page: response.page,
                    page_size: response.page_size,
                    total_pages: response.total_pages,
                    has_next: response.has_next,
                    has_previous: response.has_previous,
                });
                onCountChange?.(response.count || 0);
            } catch (err) {
                console.error('Error loading activity log:', err);
                setError('Failed to load activity log');
            } finally {
                setLoading(false);
            }
        };

        loadActivityLog();
    }, [campaignId, campaignRequestId, currentPage, refreshKey]);

    const formatDate = (dateString: string) => {
        return formatDateIfValid(dateString, 'M/d/yy');
    };

    const getModelDisplayName = (modelName: string): string => {
        const modelNames: Record<string, string> = {
            campaign: 'Campaign',
            patient: 'Patient',
            person: 'Campaign',  // Person changes are displayed as Campaign changes in UI
            teammember: 'Team Member',
            application: 'Application',
            campaignrequest: 'Campaign Request',
        };
        return modelNames[modelName?.toLowerCase()] || modelName || 'Campaign';
    };

    const getActionLabel = (entry: ActivityLogEntry): string => {
        // Team-member events get a fixed terse title; the per-person detail
        // is shown in the description below to avoid duplicating it.
        if (entry.event_type === 'team_member_added') {
            return 'added team member';
        }
        if (entry.event_type === 'team_member_removed') {
            return 'removed team member';
        }

        // For custom events (not field_change), use changes_text (has full detail) or title
        if (entry.event_type && entry.event_type !== 'field_change') {
            if (entry.changes_text) {
                return entry.changes_text;
            }
            if (entry.title && entry.title !== 'Activity') {
                return entry.title;
            }
        }

        // Check if it's a status change - display as "Status set to X"
        if (entry.metadata?.grouped_changes?.Status) {
            const statusChanges = entry.metadata.changes.filter(
                (c) => c.category === 'Status'
            );
            if (statusChanges.length > 0) {
                const statusChange = statusChanges[0];
                return `Status set to ${statusChange.new_value || 'Unknown'}`;
            }
        }

        // Check for specific field changes that should have special labels
        if (entry.metadata?.grouped_changes?.['Campaign Settings']) {
            const changes = entry.metadata.changes.filter(
                (c) => c.category === 'Campaign Settings'
            );
            if (changes.some((c) => c.field === 'thermometer_start_date')) {
                return 'Thermometer start date changed';
            }
        }

        return 'Edited';
    };

    const isStatusOnlyChange = (entry: ActivityLogEntry): boolean => {
        // Custom events (not field_change) with a title should display as simple single line
        if (entry.event_type && entry.event_type !== 'field_change') {
            if (entry.title && entry.title !== 'Activity') {
                return true;
            }
        }

        // Status changes should always be displayed as a simple single line
        // (even if accompanied by related date fields like disbursable_date)
        if (entry.metadata?.grouped_changes?.Status) {
            return true;
        }

        // Only use simple view for Campaign Settings changes that have special labels
        // (currently only thermometer_start_date has a special label)
        const categories = Object.keys(entry.metadata?.grouped_changes || {});
        if (
            categories.length === 1 &&
            categories[0] === 'Campaign Settings' &&
            entry.metadata?.changes?.length === 1
        ) {
            const field = entry.metadata.changes[0]?.field;
            // Only return true if getActionLabel will provide a meaningful label
            if (field === 'thermometer_start_date') {
                return true;
            }
        }
        return false;
    };

    const handlePreviousPage = () => {
        if (pagination?.has_previous) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const handleNextPage = () => {
        if (pagination?.has_next) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    if (loading) {
        return (
            <div className='flex items-center justify-center py-8'>
                <div className='text-gray-500'>Loading activity log...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='flex items-center justify-center py-8'>
                <div className='text-red-500'>{error}</div>
            </div>
        );
    }

    if (entries.length === 0 && currentPage === 1) {
        return (
            <div className='flex items-center justify-center py-8'>
                <div className='text-gray-500'>No activity log entries found.</div>
            </div>
        );
    }

    return (
        <div>
            {/* Entry count and pagination info */}
            {pagination && pagination.count > 0 && (
                <div className='mb-4 text-[12px]/[16px] text-body'>
                    Showing {((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, pagination.count)} of {pagination.count} entries
                </div>
            )}

            <div className='space-y-3'>
                {entries.map((entry) => {
                    const isSimpleChange = isStatusOnlyChange(entry);
                    const actionLabel = getActionLabel(entry);
                    const isCustomEvent =
                        !!entry.event_type && entry.event_type !== 'field_change';
                    const isTeamMemberEvent =
                        entry.event_type === 'team_member_added' ||
                        entry.event_type === 'team_member_removed';
                    const description = isTeamMemberEvent
                        ? normalizeTeamMemberDescription(entry.description)
                        : entry.description;

                    return (
                        <div key={entry.id} className='border-b border-stroke pb-3 last:border-b-0'>
                            {/* Header: Person name, action, and date on one line */}
                            <div className='mb-1 flex items-center justify-between'>
                                <div className='flex items-center gap-1.5 text-[12px]/[16px] text-body'>
                                    <span className='font-bold'>{entry.person_name || 'System'}</span>
                                    {entry.is_staff && (
                                        <img
                                            src={LogoIcon}
                                            alt='HHL Staff'
                                            className='inline-block'
                                            style={{ width: 14, height: 14 }}
                                        />
                                    )}
                                    <span className={isCustomEvent ? undefined : 'lowercase'}>
                                        {actionLabel}
                                    </span>
                                </div>
                                <div className='text-[12px]/[16px] text-body'>
                                    {formatDate(entry.created_date)}
                                </div>
                            </div>

                            {isSimpleChange ? (
                                <div>
                                    {description && entry.event_type !== 'status_change' && (
                                        <div className='whitespace-pre-line text-[12px]/[16px] text-[#949599]'>
                                            {description}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className='rounded bg-[#F4F4F5] px-3 py-2'>

                                    {/* Grouped changes by category with old → new values */}
                                    {(() => {
                                        const changes = entry.metadata?.changes || [];
                                        const grouped = entry.metadata?.grouped_changes || {};
                                        const isExpanded = expandedEntries.has(entry.id);
                                        const totalFieldCount = changes.length;
                                        const shouldCollapse = totalFieldCount > COLLAPSED_FIELD_LIMIT;

                                        // Build category → changes mapping for value display
                                        const categoryChanges: Record<string, ActivityLogChange[]> = {};
                                        for (const change of changes) {
                                            const cat = change.category || 'Other';
                                            if (!categoryChanges[cat]) categoryChanges[cat] = [];
                                            categoryChanges[cat].push(change);
                                        }

                                        // If no detailed changes available, fall back to grouped field names
                                        if (changes.length === 0 && Object.keys(grouped).length > 0) {
                                            return (
                                                <ul className='list-inside list-disc space-y-1'>
                                                    {Object.entries(grouped).map(([category, fields]) => (
                                                        <li key={category} className='text-[14px]/[20px]'>
                                                            <span className='font-bold'>{category}</span>
                                                            {fields && fields.length > 0 && (
                                                                <ul className='ml-6 mt-1 list-inside list-disc'>
                                                                    {fields.map((field: string, idx: number) => (
                                                                        <li key={idx} className='text-[13px]/[18px]'>{field}</li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            );
                                        }

                                        let fieldIndex = 0;
                                        const categoryKeys = Object.keys(categoryChanges);
                                        const isSingleCategory = categoryKeys.length === 1;

                                        const renderChangeItem = (change: ActivityLogChange, idx: number) => {
                                            if (LONG_TEXT_FIELDS.has(change.field)) {
                                                return (
                                                    <li key={idx} className='text-[12px]/[16px]'>
                                                        {change.field_label}
                                                        <span className='text-[#949599]'> (updated)</span>
                                                    </li>
                                                );
                                            }

                                            const oldVal = formatChangeValue(change.old_value);
                                            const newVal = formatChangeValue(change.new_value);
                                            const hasValues = oldVal || newVal;

                                            return (
                                                <li key={idx} className='text-[12px]/[16px]'>
                                                    {change.field_label}
                                                    {hasValues && (
                                                        <span className='text-[#949599]'>
                                                            {': '}
                                                            {oldVal || <em>blank</em>}
                                                            {' → '}
                                                            {newVal || <em>blank</em>}
                                                        </span>
                                                    )}
                                                </li>
                                            );
                                        };

                                        return (
                                            <>
                                                {isSingleCategory ? (
                                                    // Single category: show category as a label, fields flat
                                                    <>
                                                        <div className='mb-1 text-[12px]/[16px] font-bold'>{categoryKeys[0]}</div>
                                                        <ul className='list-inside list-disc space-y-0.5'>
                                                            {(() => {
                                                                const visibleChanges: ActivityLogChange[] = [];
                                                                for (const change of categoryChanges[categoryKeys[0]]) {
                                                                    if (!shouldCollapse || isExpanded || fieldIndex < COLLAPSED_FIELD_LIMIT) {
                                                                        visibleChanges.push(change);
                                                                    }
                                                                    fieldIndex++;
                                                                }
                                                                return visibleChanges.map((change, idx) => renderChangeItem(change, idx));
                                                            })()}
                                                        </ul>
                                                    </>
                                                ) : (
                                                    // Multiple categories: nested structure
                                                    <ul className='list-inside list-disc space-y-0.5'>
                                                        {Object.entries(categoryChanges).map(([category, catChanges]) => {
                                                            const visibleChanges: ActivityLogChange[] = [];
                                                            for (const change of catChanges) {
                                                                if (!shouldCollapse || isExpanded || fieldIndex < COLLAPSED_FIELD_LIMIT) {
                                                                    visibleChanges.push(change);
                                                                }
                                                                fieldIndex++;
                                                            }

                                                            if (visibleChanges.length === 0) return null;

                                                            return (
                                                                <li key={category} className='text-[12px]/[16px]'>
                                                                    <span className='font-bold'>{category}</span>
                                                                    <ul className='ml-5 mt-0.5 list-inside list-disc'>
                                                                        {visibleChanges.map((change, idx) => renderChangeItem(change, idx))}
                                                                    </ul>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                )}
                                                {shouldCollapse && (
                                                    <button
                                                        onClick={() => {
                                                            setExpandedEntries((prev) => {
                                                                const next = new Set(prev);
                                                                if (next.has(entry.id)) {
                                                                    next.delete(entry.id);
                                                                } else {
                                                                    next.add(entry.id);
                                                                }
                                                                return next;
                                                            });
                                                        }}
                                                        className='mt-2 text-[12px]/[16px] font-medium text-primary hover:underline'
                                                    >
                                                        {isExpanded
                                                            ? 'Show less'
                                                            : `Show ${totalFieldCount - COLLAPSED_FIELD_LIMIT} more`}
                                                    </button>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Pagination controls */}
            {pagination && pagination.total_pages > 1 && (
                <div className='mt-4 flex items-center justify-between border-t border-stroke pt-4'>
                    <button
                        onClick={handlePreviousPage}
                        disabled={!pagination.has_previous}
                        className='rounded-full border-[2px] px-[16px] py-[5px] text-[12px]/[16px] font-bold uppercase transition-colors duration-300 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-gray-300 disabled:hover:bg-transparent disabled:hover:text-black'
                    >
                        Previous
                    </button>
                    <span className='text-[12px]/[16px] text-body'>
                        Page {currentPage} of {pagination.total_pages}
                    </span>
                    <button
                        onClick={handleNextPage}
                        disabled={!pagination.has_next}
                        className='rounded-full border-[2px] px-[16px] py-[5px] text-[12px]/[16px] font-bold uppercase transition-colors duration-300 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-gray-300 disabled:hover:bg-transparent disabled:hover:text-black'
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default ActivityLog;
